import { cn, Link } from "@beweegbaas/ui";
import { createFileRoute, notFound, Outlet, redirect, useMatches } from "@tanstack/react-router";
import { containerStyles } from "#/components/page";
import { DefaultLayout } from "#/layouts/layout.default";
import { doesSchoolSlugExistInMySchools } from "#/modules/generic.user/api.user";
import { rememberSelectedSchool } from "#/modules/generic.user/user.functions";
import { getLockedStudentLessonRedirect } from "#/modules/les.leerling-gestuurd/guard.locked-student-lesson";
import { roosterQueryOptions } from "#/modules/les.planning/query.planning";
import { Rondleiding } from "#/modules/les.planning/rondleiding/rondleiding";
import { getSchoolLayoutData } from "#/modules/school/school.functions";
import { SystemNotificationPopup } from "#/modules/system-notifications/components/system-notification-popup";
import { isPreloadForOtherTenant } from "#/modules/util.fetch-client/client-router";
import { isSchoolyearSegment } from "#/modules/util.helpers/utils.schoolyear-path";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school")({
  // `/nl/2025-2026` is that schooljaar's home, not a school called "2025-2026". Refusing the slug
  // lets the router match the `{-$schoolyear}` index instead.
  params: { parse: (params) => (isSchoolyearSegment(params.school) ? false : params) },
  beforeLoad: ({ context, params, location, preload }) => {
    // Another school's or schooljaar's page is only loaded once it is visited: its loaders would
    // run with this page's tenant. The router drops a preload's notFound.
    if (preload && isPreloadForOtherTenant(params)) throw notFound();

    const { scholen, isAdmin, requires2FA, lockedLes, lastSelectedSchoolSlug } = context;

    // Locked student-lesson guard (portal's `lockedStudentLessonGuard`, which runs first): while a
    // student-led lesson has locked the session, every other school page bounces back into it.
    // The redirect target comes from the session-recorded `lockedLes` (not the eventually-consistent
    // backend `activeLessen` — dex q7hqg0da).
    const lockedLessonRedirect = getLockedStudentLessonRedirect({
      lockedLes,
      pathname: location.pathname,
      locale: params.locale,
      schoolyear: params.schoolyear,
    });
    if (lockedLessonRedirect) {
      throw redirect(lockedLessonRedirect);
    }

    // 2FA guard (portal's `twoFactorAuthGuardMiddleware`, which runs before the loader): a user
    // that still needs to set up 2FA is sent to the setup route before anything else.
    if (requires2FA) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/setup-2fa",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }

    if (!isAdmin && !doesSchoolSlugExistInMySchools(scholen, params.school)) {
      const fallbackSchool =
        scholen.find((school) => school.slug === lastSelectedSchoolSlug) ?? scholen[0]!;
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lessons/today",
        params: {
          locale: params.locale,
          schoolyear: params.schoolyear,
          school: fallbackSchool.slug,
        },
      });
    }

    // Not ported (portal parity gaps): Sentry `traced()` wrappers.

    const routeContext = { school: params.school, scholen, isAdmin, lastSelectedSchoolSlug };

    // Persist the visited school here, not in the loader: a hover-preloaded match skips the loader
    // on the click that follows. Never on preloads, never for a slug outside the user's own
    // scholen (admins browse foreign schools). No promise unless there is something to write, or
    // the layout swaps to its pendingComponent on every navigation.
    if (
      !preload &&
      params.school !== lastSelectedSchoolSlug &&
      doesSchoolSlugExistInMySchools(scholen, params.school)
    ) {
      return rememberSelectedSchool({ data: { schoolSlug: params.school } }).then(
        () => routeContext,
      );
    }

    return routeContext;
  },
  loader: async ({ context, params, location }) => {
    const [layoutData] = await Promise.all([
      getSchoolLayoutData(),
      // Whether the docent has a rooster decides where every start button leads, from the first
      // render on.
      context.queryClient.prefetchQuery(
        roosterQueryOptions({ school: params.school, schoolyear: params.schoolyear }),
      ),
    ]);

    // Onboarding hard guard: until the user dismisses the onboarding page (its CTA marks
    // `hasSeenOnboarding`), a school without imported leerlingen only allows the onboarding page
    // itself, the import wizard (the way out) and end-test-period (trial schools must be able to
    // subscribe to gain import rights). Everything else bounces back to the onboarding.
    const onGuardExemptPage = ["/onboarding", "/detail/import", "/end-test-period"].some(
      (segment) => location.pathname.includes(segment),
    );
    if (
      !context.isAdmin &&
      !context.hasSeenOnboarding &&
      layoutData.showImportReminder &&
      !onGuardExemptPage
    ) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/onboarding",
        params: { locale: params.locale, schoolyear: params.schoolyear, school: params.school },
      });
    }

    return {
      scholen: context.scholen,
      isAdmin: context.isAdmin,
      ...layoutData,
    };
  },
  component: SchoolLayout,
});

function SchoolLayout() {
  const loaderData = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();

  // Portal's `handle.layout === false` opt-out, ported to TanStack `staticData`.
  const matches = useMatches();
  const showLayout = !matches.some((match) => match.staticData?.layout === false);

  // The banner nags towards the import wizard — pointless while the user is already in it.
  const onImportPage = matches.some((match) => match.routeId.includes("/detail/import"));

  if (!showLayout) {
    return <Outlet />;
  }

  // Banners render inside the sidebar inset: the desktop sidebar is fixed-positioned, so
  // anything above the SidebarProvider would misalign the chrome.
  return (
    <DefaultLayout>
      {/* No loader data when the layout draws the not-found page for an unknown URL. */}
      {loaderData?.showImportReminder && !onImportPage ? (
        <div
          className={cn(
            "hidden sm:flex",
            "sticky top-0 z-40 w-full p-1 text-sm",
            "rounded-none border-x-0 border-t-0",
          )}
        >
          <div
            className={cn(
              `bg-primary/80 squircle flex min-h-9 items-baseline gap-2 rounded py-1 text-white shadow backdrop-blur-sm supports-squircle:rounded-xl`,
              containerStyles({ container: "default" }),
            )}
          >
            <IconMaterialSymbolsInfo className="relative top-[.2em] shrink-0" />
            <div className="flex grow items-center gap-4">
              <span>Je moet eerst leerlingen importeren voordat je de app kunt gebruiken.</span>
              <Link
                variant="white"
                underline
                to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                params={{ locale, schoolyear, school, type: "modify-existing-year" }}
                className="flex items-center gap-1 py-2"
              >
                Importeer leerlingen
                <IconMaterialSymbolsArrowRightAlt />
              </Link>
            </div>
          </div>
        </div>
      ) : null}
      <SystemNotificationPopup />
      <Outlet />
      <Rondleiding />
    </DefaultLayout>
  );
}
